import { Box, Card, VStack } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import mqtt from 'mqtt'
import { useEffect, useRef, useState } from 'react'
import Chat from '~/components/Chat'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/factory/bot')({
  component: FactoryBotPage,
})

const hivemqws = import.meta.env.VITE_CENTRAL_WS_ADDRESS

interface TopicNode {
  name: string
  attributes: {
    index: number
    value?: string | number
  }
  children?: TopicNode[]
}

const topicsRoot: TopicNode = {
  name: 'Root',
  attributes: {
    index: 0,
  },
  children: [],
}

let uns_client: mqtt.MqttClient | null = null

function FactoryBotPage() {
  const introMessage =
    "Hi, I'm FactoBot, feel free to ask me anything about the data in your Industry Unified Namespace."
  const promptinit = `ROLE
You are an analytics expert for an industrial Unified Namespace (UNS). You analyse live MQTT telemetry from a manufacturing operation.

CONTEXT
The data covers production lines across three plants: Tokyo, SanFrancisco, Nuremberg. Each plant runs a mixer line, a filling line, a storage line and a packing line. Telemetry units in the dataset:
- temperature in degrees Celsius
- humidity as a percentage
- pressure in psi
- weight in kilograms
- agitator speed in rpm

ANSWER RULES
- Answer only from the dataset below. Never invent values, assets, plants, or fields that are not present.
- Lead with the result: one short sentence, a small bullet list, or a compact markdown table. Pick whichever is clearest for the question.
- Follow the result with at most 2 to 4 sentences of plain-language explanation. Do not exceed that.
- Round numeric values to two decimals where appropriate. Always include units (psi, kg, %, rpm, °C).
- For comparisons across plants or work centres, prefer a markdown table to prose.
- If the dataset does not contain enough information to answer, say so in one sentence and stop.
- Never include code, pseudocode, SQL, JSON, schemas, formulas, or step-by-step calculations in your response. The user wants the conclusion, not the method.
- No greetings, no caveats, no mention of being an AI, no emojis.

DATASET (live MQTT topic tree, JSON):
`

  const questionList = [
    'What is the average OEE for each plant (Tokyo, SanFrancisco, Nuremberg)? Which plant is the most efficient?',
    'For each work center, how does the rateactual compare to the ratestandard? Which are underperforming?',
    'What is the total countdefect for each filler and labeler? Calculate the defect rate as a percentage of countinfeed.',
    'What is the total timedownunplanned vs timedownplanned for each production line?',
    'What are the current temperature and weight readings for each tank across all plants?',
    'Compare the performance of the Filler work center across Tokyo, SanFrancisco, and Nuremberg.',
    'Which production lines have the highest unplanned downtime? What are the most common states?',
  ]

  const uns_options: mqtt.IClientOptions = {
    protocol: 'wss',
    port: 443,
    keepalive: 20,
    clientId: `stories_uns_factory_${Math.random().toString(16).substr(2, 8)}`,
    username: import.meta.env.VITE_WS_USERNAME,
    password: import.meta.env.VITE_WS_PASSWORD,
  }

  const [topics, setTopics] = useState<TopicNode>(topicsRoot)
  const [isConnected, setIsConnected] = useState(false)
  const [messagesPerSecond, setMessagesPerSecond] = useState(0)
  const messageCountRef = useRef(0)
  const lastCountRef = useRef(0)
  const mpsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function connect_mqtt(broker: string) {
    uns_client?.end()
    uns_client = mqtt.connect(`wss://${broker}:443/mqtt`, uns_options)

    uns_client.on('message', (topic, message) => {
      messageCountRef.current++
      updateTreeWithNewNode(topic.toString(), message.toString())
    })

    uns_client.on('connect', () => {
      console.log(`Connected to ${broker}`)
      setIsConnected(true)
      uns_client?.subscribe('BBCorp/#', { qos: 1 }, (err) => {
        if (err) {
          console.log(err)
        }
      })
    })

    uns_client.on('close', () => {
      setIsConnected(false)
    })

    uns_client.on('error', (err) => {
      console.log('Error happened')
      console.log(err)
    })
  }

  async function disconnect_mqtt(client: mqtt.MqttClient | null) {
    await client?.end()
    console.log('Disconnected')
  }

  function updateTreeWithNewNode(topic: string, message: string) {
    let parsedValue: unknown
    try {
      const parsed = JSON.parse(message)
      parsedValue = parsed.value ?? parsed
    } catch {
      parsedValue = message
    }

    const segments = topic.split('/')
    const nextData = Object.assign({}, topics)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let currentNode: any = nextData
    segments.forEach((segment: string) => {
      if (!currentNode.children) {
        currentNode.children = []
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let existingNode = currentNode.children.find((n: any) => n.name === segment)
      if (!existingNode) {
        const newNode = {
          name: segment,
          attributes: { index: 1, value: parsedValue },
        }
        currentNode.children.push(newNode)
        existingNode = newNode
      } else {
        existingNode.attributes.index = existingNode.attributes.index + 1
        existingNode.attributes.value = parsedValue
      }
      currentNode = existingNode
    })
    setTopics(nextData)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: connect/disconnect run once on mount by design, not on every render
  useEffect(() => {
    if (hivemqws) {
      connect_mqtt(hivemqws)
    }
    mpsIntervalRef.current = setInterval(() => {
      const current = messageCountRef.current
      const mps = current - lastCountRef.current
      lastCountRef.current = current
      setMessagesPerSecond(mps)
    }, 1000)
    return () => {
      disconnect_mqtt(uns_client)
      if (mpsIntervalRef.current) {
        clearInterval(mpsIntervalRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box p={6} h="calc(100vh - 120px)">
      <VStack align="stretch" gap={4} h="100%">
        <PageHeader
          title="Factory - AI Bot"
          subtitle="Ask AI to extract information from the UNS"
        />

        <Card.Root flex={1} overflow="hidden" boxShadow="2xl" display="flex" flexDirection="column">
          <Card.Body overflow="auto" flex="1" minH={0}>
            <Box h="100%">
              <Chat
                introMessage={introMessage}
                promptinit={promptinit}
                topics={topics}
                questionList={questionList}
                isConnected={isConnected}
                messagesPerSecond={messagesPerSecond}
              />
            </Box>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
